import React from 'react'
import { timeDay } from 'd3-time'
import locale from 'd3-time-format/locale/de-CH.json'
import { timeFormat, timeFormatDefaultLocale } from 'd3-time-format'
timeFormatDefaultLocale(locale)

import Timeline from 'components/Timeline'
import { Link } from '@ta-interaktiv/contentbricks'
import { Waypoint } from 'react-waypoint'

import './styles.css'

const VARS = [
  {
    title: 'Wie hoch ist die Impfbereitschaft?',
    summary: 'Anteil der Bevölkerung, der bereit ist, sich impfen zu lassen.',
    default: 1,
    fields: [
      {
        value: 0.5,
        label: '50%',
        descr: <>Pessimistisches Szenario</>,
        descrSelected: <>Pessimistisches Szenario</>
      },
      {
        value: 0.65,
        label: '65%',
        descr: <>Mittleres Szenario</>,
        descrSelected: <>Mittleres Szenario</>
      },
      {
        value: 0.75,
        label: '75%',
        descr: <>Max. Zielwert des BAG</>,
        descrSelected: <>Max. Zielwert des BAG</>
      }
    ]
  },
  {
    title: 'Wann beginnt das Impfen für die breite Bevölkerung?',
    summary:
      'Sobald alle besonders gefährdeten Personen mindestens einmal geimpft ist, kann die Impfung der übrigen Bevölkerung beginnen.',
    default: 0,
    fields: [
      {
        value: '2021-05-15',
        label: '15. Mai',
        descrSelected: (
          <>
            <Link href='https://www.tagesanzeiger.ch/liefern-die-hersteller-schneller-als-erwartet-836133528511'>
              Gemäss BAG
            </Link>{' '}
            sollten die Risikogruppen bis Ende April einfach und bis Mitte Mai
            grösstenteils vollständig geimpft sein
          </>
        ),
        descr: (
          <>
            Gemäss BAG sollten die Risikogruppen bis Ende April einfach und bis
            Mitte Mai grösstenteils vollständig geimpft sein{' '}
          </>
        )
      },
      {
        value: '2021-05-30',
        label: '30. Mai',
        descr: <>Szenario bei logistischen Problemen oder Lieferengpässen</>,
        descrSelected: (
          <>Szenario bei logistischen Problemen oder Lieferengpässen</>
        )
      }
    ]
  },
  {
    title: 'Wie viele Personen können ab Mai pro Tag geimpft werden?',
    summary: 'Verfügbare Impfdosen pro Tag',
    default: 1,
    fields: [
      {
        value: 50000,
        label: '50’000',
        descr: <>Szenario bei Liefer&shy;engpässen</>,
        descrSelected: <>Szenario bei Liefer&shy;engpässen</>
      },
      {
        value: 70000,
        label: '70’000',
        descr: <>Schätzung des BAG von Anfang Jahr</>,
        descrSelected: (
          <>
            Schätzung{' '}
            <Link
              href='https://www.tagesanzeiger.ch/impfaktion-in-der-schweiz-startet-anfang-januar-588978011797'
              allowWordWrap
            >
              des BAG
            </Link>{' '}
            von Anfang Jahr
          </>
        )
      },
      {
        value: 110000,
        label: '110’000',
        descrSelected: (
          <>
            Umfrage bei den{' '}
            <Link
              href='https://nzzas.nzz.ch/schweiz/der-schweizer-impfplan-es-wird-eng-ld.1606559'
              allowWordWrap
            >
              Kantonen
            </Link>
            , genügend Dosen vorausgesetzt
          </>
        ),
        descr: <>Umfrage bei den Kantonen, genügend Dosen vorausgesetzt</>
      }
    ]
  },
  {
    title: 'An wie vielen Tagen pro Woche wird geimpft?',
    default: 1,
    fields: [
      {
        value: false,
        label: '5 Tage',
        descr: (
          <>
            Im Kanton Zürich sollen bspw. die Impfzentren nur von Montag bis
            Freitag geöffnet sein
          </>
        ),
        descrSelected: (
          <>
            Im Kanton Zürich sollen bspw. die Impfzentren nur{' '}
            <Link
              href='https://www.nzz.ch/zuerich/corona-impfstoff-in-zuerich-wegen-fehlgeleiteter-post-entsorgt-ld.1607000'
              allowWordWrap
            >
              Montag bis Freitag
            </Link>{' '}
            geöffnet sein
          </>
        )
      },
      {
        value: true,
        label: '7 Tage',
        descr: <>Bei Bedarf soll auch ein Wochenendbetrieb möglich sein</>,
        descrSelected: (
          <>Bei Bedarf soll auch ein Wochenendbetrieb möglich sein</>
        )
      }
    ]
  }
]

function Variable({ title, summary, fields = [], selected, onChange }) {
  return (
    <div className='variable'>
      <h4>{title}</h4>
      <div className={`grid grid${fields.length}`} role='button'>
        {fields.map(({ value, label, descr, descrSelected }, i) => (
          <div
            className={`grid-tile ${i == selected ? 'selected' : ''}`}
            onClick={() => onChange(i)}
          >
            <div className='label'>{label}</div>
            <div className={`source`}>
              {i == selected ? descrSelected : descr}
            </div>
          </div>
        ))}
        {fields.map((f, i) => (
          <div style={{ margin: 'auto' }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 12,
                border: '1px solid #d4d4d4'
              }}
              onClick={() => onChange(i)}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 10,
                  border: '2px solid #fff',
                  background: i == selected ? '#d4d4d4' : '#fff'
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function VaccinationCalculator() {
  const [selected, setSelected] = React.useState(VARS.map(v => v.default || 0))

  const containerRef = React.useRef(null)
  const [showResult, setShowResult] = React.useState(false)

  const people = 4292471
  const vaccWillingness = VARS[0].fields[selected[0]].value
  const vaccStart = new Date(VARS[1].fields[selected[1]].value)
  const vaccCapacity = VARS[2].fields[selected[2]].value
  const vaccOpen = VARS[3].fields[selected[3]].value
  const vaccSecondJab = 28

  const vaccPeople = vaccWillingness * people
  const vaccDays = Math.round(vaccPeople / (vaccCapacity / 2))

  const vaccOffset = Math.max(vaccDays / 2, vaccDays - vaccSecondJab)
  const weekendDays = timeDay
    .range(vaccStart, timeDay.offset(vaccStart, vaccOffset))
    .filter(d => d.getDay() == 0 || d.getDay() == 6).length
  const vaccEnd = timeDay.offset(
    vaccStart,
    vaccOffset + (vaccOpen ? 0 : weekendDays)
  )

  const params = { vaccStart, vaccEnd }

  return (
    <div ref={containerRef} className='wrapper ta chart'>
      {VARS.map((v, i) => (
        <>
          <Variable
            {...v}
            selected={selected[i]}
            onChange={option => {
              setShowResult(true)
              setSelected(selected => {
                const nextSelected = [...selected]
                nextSelected[i] = option
                return nextSelected
              })
            }}
          />
          {i == 1 && (
            <Waypoint
              scrollableAncestor={window}
              onEnter={({ previousPosition, currentPosition, event }) => {
                setShowResult(true)
              }}
            />
          )}
        </>
      ))}
      <div className={`sticky ${showResult ? 'show' : ''}`}>
        <div>
          <div>
            Alle impfbereiten Erwachsenen mindestens einmal geimpft am
            <br />
            <strong>{timeFormat('%-d. %B')(vaccEnd)}</strong>
          </div>
          <div className='small'>
            Vollständig geimpft sind sie 4 Wochen später.
          </div>
        </div>
      </div>
      <Timeline {...params} />
    </div>
  )
}
